import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme,
  useMediaQuery,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';

const SummaryCards = ({ summary, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const stats = summary?.summary || {};

  // Calculate percentage changes (mock data - you can replace with actual calculations)
  const calculateChange = (value) => {
    // This is a mock calculation - replace with actual data from your API
    const percentage = (Math.random() * 20 - 10).toFixed(1);
    return {
      value: `${Math.abs(percentage)}%`,
      isPositive: parseFloat(percentage) >= 0,
    };
  };

  const cards = [
    {
      id: 'total',
      title: 'Total Transactions',
      value: stats.total_transactions || 0,
      icon: <Receipt sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, color: theme.palette.primary.main }} />,
      color: theme.palette.primary.main,
      bgColor: theme.palette.primary.main + '15',
      change: calculateChange(stats.total_transactions),
      subtitle: 'Total transactions processed',
    },
    {
      id: 'paid_in',
      title: 'Total Paid In',
      value: formatCurrency(stats.total_paid_in || 0),
      icon: <TrendingUp sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, color: theme.palette.success.main }} />,
      color: theme.palette.success.main,
      bgColor: theme.palette.success.main + '15',
      change: calculateChange(stats.total_paid_in),
      subtitle: 'Money received',
    },
    {
      id: 'withdrawn',
      title: 'Total Withdrawn',
      value: formatCurrency(stats.total_withdrawn || 0),
      icon: <TrendingDown sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, color: theme.palette.error.main }} />,
      color: theme.palette.error.main,
      bgColor: theme.palette.error.main + '15',
      change: calculateChange(stats.total_withdrawn),
      subtitle: 'Money sent out',
    },
    {
      id: 'balance',
      title: 'Net Balance',
      value: formatCurrency((stats.total_paid_in || 0) - (stats.total_withdrawn || 0)),
      icon: <AccountBalance sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, color: theme.palette.warning.main }} />,
      color: theme.palette.warning.main,
      bgColor: theme.palette.warning.main + '15',
      change: calculateChange((stats.total_paid_in || 0) - (stats.total_withdrawn || 0)),
      subtitle: 'Current balance',
    },
  ];

  // Determine card grid sizes based on screen
  const getGridSizes = () => {
    if (isMobile) {
      return { xs: 12, sm: 12 };
    }
    if (isTablet) {
      return { xs: 12, sm: 6 };
    }
    return { xs: 12, sm: 6, md: 3 };
  };

  const gridSizes = getGridSizes();

  // Render loading skeleton
  if (loading) {
    return (
      <Grid container spacing={isMobile ? 1.5 : 3}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item {...gridSizes} key={item}>
            <Card sx={{
              height: '100%',
              borderRadius: 2,
              boxShadow: theme.shadows[1],
            }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="80%" height={40} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="40%" height={15} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={isMobile ? 1.5 : 3}>
      {cards.map((card) => (
        <Grid item {...gridSizes} key={card.id}>
          <Card
            sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: theme.shadows[2],
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-6px)',
                boxShadow: theme.shadows[6],
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: card.color,
                opacity: 0.8,
              },
            }}
          >
            <CardContent sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexDirection: isMobile ? 'row' : 'row',
              }}>
                <Box sx={{
                  flex: 1,
                  minWidth: 0, // Prevents overflow
                }}>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    component="div"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.2,
                      mt: 0.5,
                      wordBreak: 'break-word',
                    }}
                  >
                    {card.value}
                  </Typography>
                  {!isMobile && card.subtitle && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      {card.subtitle}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    backgroundColor: card.bgColor,
                    borderRadius: '50%',
                    p: { xs: 0.75, sm: 1, md: 1.25 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    ml: 1,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  {card.icon}
                </Box>
              </Box>

              {/* Change Indicator - Mobile friendly */}
              <Box sx={{
                mt: { xs: 1, sm: 1.5, md: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 0.5,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    size="small"
                    label={card.change.value}
                    icon={card.change.isPositive ?
                      <ArrowUpward sx={{ fontSize: 12 }} /> :
                      <ArrowDownward sx={{ fontSize: 12 }} />
                    }
                    sx={{
                      height: { xs: 20, sm: 24 },
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      backgroundColor: card.change.isPositive ?
                        theme.palette.success.main + '20' :
                        theme.palette.error.main + '20',
                      color: card.change.isPositive ?
                        theme.palette.success.main :
                        theme.palette.error.main,
                      '& .MuiChip-icon': {
                        fontSize: { xs: 12, sm: 14 },
                      },
                    }}
                  />
                  {!isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      vs last month
                    </Typography>
                  )}
                </Box>

                {isMobile && (
                  <Typography variant="caption" color="text.secondary">
                    Last 30 days
                  </Typography>
                )}
              </Box>

              {/* Progress bar for visual indicator */}
              {card.id === 'balance' && (
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(Math.abs((stats.total_paid_in || 0) - (stats.total_withdrawn || 0)) / 1000, 100)}
                    sx={{
                      height: 3,
                      borderRadius: 1,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: (stats.total_paid_in || 0) > (stats.total_withdrawn || 0) ?
                          theme.palette.success.main :
                          theme.palette.error.main,
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default SummaryCards;